import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  Users, Calendar, Package, AlertCircle, Plus, Bell, ChevronRight,
  User, MessageSquare, Wrench, UtensilsCrossed, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PageLayout from '../components/PageLayout';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [trips, setTrips] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [newRequests, setNewRequests] = useState([]);
  const [maintenanceSummary, setMaintenanceSummary] = useState({ overdue: [], upcoming: [] });
  const [pendingFoodFun, setPendingFoodFun] = useState([]);
  const [failedEmailCount, setFailedEmailCount] = useState(0);
  const [pendingLicenses, setPendingLicenses] = useState(0);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, tripsRes, prospectsRes, questionsRes, rosterRes, maintRes, foodFunRes, failedRes, licensesRes] = await Promise.all([
          api.get('/stats'),
          api.get('/trips'),
          api.get('/prospects?status=new'),
          api.get('/qa/pending'),
          api.get(`/participants/roster?year=${new Date().getFullYear()}`).catch(() => ({ data: [] })),
          api.get('/maintenance-tasks/summary').catch(() => ({ data: { overdue: [], upcoming: [] } })),
          api.get('/food-fun-pending').catch(() => ({ data: [] })),
          api.get('/admin/failed-emails/count').catch(() => ({ data: { count: 0 } })),
          api.get('/admin/licenses').catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        setTrips((tripsRes.data || []).sort((a, b) => new Date(a.start_date) - new Date(b.start_date)));
        setProspects(prospectsRes.data);
        setPendingQuestions(questionsRes.data);
        setNewRequests(rosterRes.data.filter(p => p.participant_status === 'requested'));
        setMaintenanceSummary(maintRes.data);
        setPendingFoodFun(foodFunRes.data || []);
        setFailedEmailCount(failedRes.data?.count || 0);
        setPendingLicenses((licensesRes.data || []).filter(l => l.status === 'pending').length);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const headerActions = (
    <Link to="/admin/trips">
      <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="create-trip-btn">
        <Plus className="w-4 h-4 mr-2" />New Trip
      </Button>
    </Link>
  );

  return (
    <PageLayout title="Admin Dashboard" headerActions={headerActions}>
        <div className="space-y-6">

          {/* ── Main Content Grid: Trips + Pending Actions ── */}
          <div className="grid lg:grid-cols-3 gap-6">

            {/* Trips Overview */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Trips Overview</CardTitle>
                  <Link to="/admin/trips">
                    <Button variant="ghost" size="sm" className="text-primary">
                      Manage All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {trips.length > 0 ? (
                  <div className="space-y-3">
                    {trips.slice(0, 5).map((trip) => (
                      <div key={trip.trip_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div>
                          <p className="font-medium text-slate-800">{trip.name}</p>
                          <p className="text-sm text-slate-500">{trip.start_date} – {trip.end_date}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{trip.participant_count || 0} / {trip.max_participants || 35} participants</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={
                            trip.status === 'registration_open' ? 'bg-status-success/10 text-status-success' :
                            trip.status === 'active' ? 'bg-primary/10 text-primary' :
                            'bg-slate-100 text-slate-600'
                          }>
                            {trip.status.replace('_', ' ')}
                          </Badge>
                          <Link to={`/trips/${trip.trip_id}`}>
                            <Button variant="ghost" size="sm"><ChevronRight className="w-4 h-4" /></Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">No trips created yet</p>
                    <Link to="/admin/trips">
                      <Button className="mt-4 rounded-full" size="sm">
                        <Plus className="w-4 h-4 mr-2" />Create First Trip
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Pending Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">

                {/* New Requests */}
                <Link to="/admin/participants" className="block" data-testid="pending-requests-link">
                  <div className={`flex items-center justify-between p-4 rounded-xl transition-colors ${newRequests.length > 0 ? 'bg-orange-50 hover:bg-orange-100' : 'bg-slate-50 hover:bg-slate-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${newRequests.length > 0 ? 'bg-orange-100' : 'bg-slate-100'}`}>
                        <Users className={`w-5 h-5 ${newRequests.length > 0 ? 'text-orange-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">New Requests</p>
                        <p className="text-sm text-slate-500">{newRequests.length} awaiting invitation</p>
                      </div>
                    </div>
                    {newRequests.length > 0
                      ? <Badge className="bg-orange-100 text-orange-700 border-0">{newRequests.length}</Badge>
                      : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </div>
                </Link>

                {/* New Prospects */}
                <Link to="/admin/participants?tab=prospects" className="block" data-testid="pending-prospects-link">
                  <div className={`flex items-center justify-between p-4 rounded-xl transition-colors ${prospects.length > 0 ? 'bg-amber-50 hover:bg-amber-100' : 'bg-slate-50 hover:bg-slate-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${prospects.length > 0 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                        <User className={`w-5 h-5 ${prospects.length > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">New Prospects</p>
                        <p className="text-sm text-slate-500">{prospects.length} awaiting review</p>
                      </div>
                    </div>
                    {prospects.length > 0
                      ? <Badge className="bg-amber-100 text-amber-700 border-0">{prospects.length}</Badge>
                      : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </div>
                </Link>

                {/* Q&A Questions */}
                <Link to="/admin/qa" className="block">
                  <div className={`flex items-center justify-between p-4 rounded-xl transition-colors ${pendingQuestions.length > 0 ? 'bg-amber-50 hover:bg-amber-100' : 'bg-slate-50 hover:bg-slate-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${pendingQuestions.length > 0 ? 'bg-amber-100' : 'bg-status-info/10'}`}>
                        <MessageSquare className={`w-5 h-5 ${pendingQuestions.length > 0 ? 'text-amber-600' : 'text-status-info'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">Q&A Questions</p>
                        <p className="text-sm text-slate-500">{pendingQuestions.length} need{pendingQuestions.length !== 1 ? '' : 's'} answer</p>
                      </div>
                    </div>
                    {pendingQuestions.length > 0
                      ? <Badge className="bg-amber-100 text-amber-700 border-0">{pendingQuestions.length}</Badge>
                      : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </div>
                </Link>

                {/* Licenses Pending Review */}
                <Link to="/admin/licenses" className="block">
                  <div className={`flex items-center justify-between p-4 rounded-xl transition-colors ${pendingLicenses > 0 ? 'bg-amber-50 hover:bg-amber-100' : 'bg-slate-50 hover:bg-slate-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${pendingLicenses > 0 ? 'bg-amber-100' : 'bg-primary/10'}`}>
                        <ShieldCheck className={`w-5 h-5 ${pendingLicenses > 0 ? 'text-amber-600' : 'text-primary'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">Licenses</p>
                        <p className="text-sm text-slate-500">
                          {pendingLicenses > 0 ? `${pendingLicenses} pending review` : 'All reviewed'}
                        </p>
                      </div>
                    </div>
                    {pendingLicenses > 0 && <Badge className="bg-amber-500 text-white border-0">{pendingLicenses}</Badge>}
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Link>

                {/* Food & Fun Pending */}
                <Link to="/food-fun" className="block">
                  <div className={`flex items-center justify-between p-4 rounded-xl transition-colors ${pendingFoodFun.length > 0 ? 'bg-amber-50 hover:bg-amber-100' : 'bg-slate-50 hover:bg-slate-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <UtensilsCrossed className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">Local Guide</p>
                        <p className="text-sm text-slate-500">{pendingFoodFun.length > 0 ? `${pendingFoodFun.length} pending review` : 'No pending submissions'}</p>
                      </div>
                    </div>
                    {pendingFoodFun.length > 0
                      ? <Badge className="bg-amber-100 text-amber-700 border-0">{pendingFoodFun.length}</Badge>
                      : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </div>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* ── Maintenance Alerts ── */}
          {(maintenanceSummary.overdue.length > 0 || maintenanceSummary.upcoming.length > 0) && (
            <Card className={maintenanceSummary.overdue.length > 0 ? 'border-red-200' : 'border-amber-200'}>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-slate-600" />
                  Maintenance
                  {maintenanceSummary.overdue.length > 0 && (
                    <Badge className="bg-red-100 text-red-700 border-0">{maintenanceSummary.overdue.length} overdue</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {maintenanceSummary.overdue.slice(0, 3).map(t => (
                    <div key={t.task_id} className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="text-sm text-red-800 flex-1">{t.title}</span>
                      <span className="text-xs text-red-500">overdue</span>
                    </div>
                  ))}
                  {maintenanceSummary.upcoming.slice(0, 3).map(t => (
                    <div key={t.task_id} className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                      <Bell className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span className="text-sm text-amber-800 flex-1">{t.title}</span>
                      <span className="text-xs text-amber-500">due soon</span>
                    </div>
                  ))}
                </div>
                <Link to="/admin/maintenance" className="block mt-3">
                  <Button variant="outline" size="sm" className="w-full rounded-full text-xs">
                    View All Tasks <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* ── Stats ── */}
          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Total Users</p>
                    <p className="text-2xl font-heading font-bold text-slate-800 mt-1">{stats.total_users || 0}</p>
                  </div>
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Active Trips</p>
                    <p className="text-2xl font-heading font-bold text-slate-800 mt-1">{stats.active_trips || 0}</p>
                  </div>
                  <div className="w-10 h-10 bg-secondary/10 rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-secondary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Supply Bags</p>
                    <p className="text-2xl font-heading font-bold text-slate-800 mt-1">{stats.supply_bags || 0}</p>
                  </div>
                  <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                    <Package className="w-5 h-5 text-accent" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Failed Emails Alert */}
            {failedEmailCount > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <span className="text-sm font-medium text-red-800">{failedEmailCount} email{failedEmailCount !== 1 ? 's' : ''} failed to send</span>
                  </div>
                  <Badge className="bg-red-100 text-red-700 border-0">{failedEmailCount}</Badge>
                </CardContent>
              </Card>
            )}
          </div>

        </div>
    </PageLayout>
  );
};

export default AdminDashboard;
